import {
	DatePicker,
	DatePickerChangeEvent,
} from "@progress/kendo-react-dateinputs";
import "./custom-date-picker.css";
import { Translate } from "../translate/translate.component";
import { useEffect, useState } from "react";

export type DatePickerDensitySize = "sm" | "md" | "lg";

interface CustomDatePickerBaseProps {
	size?: "small" | "medium" | "large";
	className?: string;
	disabled?: boolean;
	title?: string;
	placeholder?: string;
	densitySize?: DatePickerDensitySize;
	inputHeight?: number;
}

interface UncontrolledDatePickerProps extends CustomDatePickerBaseProps {
	initialValue: Date | string | undefined;
	onChange: (value: string | null) => void;
	value?: never;
	onDateChange?: never;
}

interface ControlledDatePickerProps extends CustomDatePickerBaseProps {
	value: Date | null;
	onDateChange: (value: Date | null) => void;
	initialValue?: never;
	onChange?: never;
}

type CustomDatePickerProps =
	| UncontrolledDatePickerProps
	| ControlledDatePickerProps;

/**
 * Custom date picker component wrapping Kendo DatePicker with German locale.
 *
 * Uncontrolled mode (initialValue + onChange):
 *   Manages its own state. onChange returns yyyy-mm-dd string (PostgreSQL date format).
 *
 * Controlled mode (value + onDateChange):
 *   Parent owns the state. onDateChange returns Date | null directly.
 */
export const CustomDatePicker: React.FC<CustomDatePickerProps> = (props) => {
	const isControlled =
		("value" in props && props.value !== undefined) ||
		("onDateChange" in props && props.onDateChange !== undefined);

	if (isControlled) {
		return (
			<ControlledDatePicker {...(props as ControlledDatePickerProps)} />
		);
	}
	return (
		<UncontrolledDatePicker {...(props as UncontrolledDatePickerProps)} />
	);
};

const ControlledDatePicker: React.FC<ControlledDatePickerProps> = ({
	value,
	onDateChange,
	size,
	className,
	disabled,
	title,
	placeholder,
	inputHeight,
}) => {
	const wrapperStyle = inputHeight
		? ({
				"--date-picker-height": `${inputHeight}px`,
			} as React.CSSProperties)
		: undefined;
	return (
		<Translate>
			<div
				className={
					inputHeight ? "date-picker-custom-height" : undefined
				}
				style={wrapperStyle}
			>
				<DatePicker
					value={value}
					onChange={(e: DatePickerChangeEvent) =>
						onDateChange(e.value)
					}
					size={size}
					format="dd.MM.yyyy"
					formatPlaceholder={{
						day: "Tag",
						month: "Monat",
						year: "Jahr",
					}}
					className={className}
					disabled={disabled}
					title={title}
					placeholder={placeholder}
				/>
			</div>
		</Translate>
	);
};

const UncontrolledDatePicker: React.FC<UncontrolledDatePickerProps> = ({
	initialValue,
	onChange,
	size,
	className,
	disabled,
	title,
	placeholder,
	inputHeight,
}) => {
	const [date, setDate] = useState<Date | null>(null);

	useEffect(() => {
		if (initialValue && date === null) {
			setDate(new Date(initialValue));
		}
	}, [initialValue]);

	const handleChange = (event: DatePickerChangeEvent) => {
		const date = event.value;
		setDate(date);

		if (date) {
			const utcDate = new Date(
				Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
			);
			onChange(utcDate.toISOString().split("T")[0]);
		} else {
			onChange(null);
		}
	};

	const wrapperStyle = inputHeight
		? ({
				"--date-picker-height": `${inputHeight}px`,
			} as React.CSSProperties)
		: undefined;
	return (
		<Translate>
			<div
				className={
					inputHeight ? "date-picker-custom-height" : undefined
				}
				style={wrapperStyle}
			>
				<DatePicker
					value={date}
					onChange={handleChange}
					size={size ?? "small"}
					fillMode="outline"
					format="dd.MM.yyyy"
					formatPlaceholder={{
						day: "Tag",
						month: "Monat",
						year: "Jahr",
					}}
					className={className}
					disabled={disabled}
					title={title}
					placeholder={placeholder}
				/>
			</div>
		</Translate>
	);
};
